import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, LineChart, Line,
} from 'recharts';
import { format, parseISO, subDays } from 'date-fns';
import { pt } from 'date-fns/locale';
import {
  Zap, Activity, TrendingUp, MapPin, Settings, RefreshCw, AlertCircle,
  Eye, EyeOff, Search, Filter,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter,
} from '../components/ui/table';
import ChartCard from '../components/ChartCard';
import ExportMenu from '../components/ExportMenu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#003D7A', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#65a30d'];

const RANGE_PRESETS = [
  { value: '7', label: 'Última semana', days: 7 },
  { value: '15', label: 'Últimos 15 dias', days: 15 },
  { value: '30', label: 'Últimos 30 dias', days: 30 },
  { value: '60', label: 'Últimos 60 dias', days: 60 },
  { value: '90', label: 'Últimos 3 meses', days: 90 },
  { value: '365', label: 'Último ano', days: 365 },
  { value: 'all', label: 'Todo o histórico', days: null },
];

const fmtNumber = (v, digits = 0) =>
  typeof v === 'number' && Number.isFinite(v)
    ? v.toLocaleString('pt-PT', { maximumFractionDigits: digits, minimumFractionDigits: digits })
    : '—';

const fmtKwh = (v) => `${fmtNumber(v, 0)} kWh`;
const fmtKw = (v) => `${fmtNumber(v, 1)} kW`;

const KpiCard = ({ icon: Icon, label, value, sub, accent }) => (
  <Card data-testid={`energia-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardContent className="p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500">{label}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
        </div>
        <div className={`p-3 rounded-lg ${accent || 'bg-blue-50 text-[#003D7A]'}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const Energia = () => {
  const navigate = useNavigate();
  const [rangePreset, setRangePreset] = useState('all');
  const [granularity, setGranularity] = useState('day');
  const [locais, setLocais] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [ranking, setRanking] = useState([]);
  const [serieGlobal, setSerieGlobal] = useState([]);
  const [serieByLocal, setSerieByLocal] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Multi-seleção de locais (toggle no gráfico/tabela) + filtros da tabela
  const [visibleCpes, setVisibleCpes] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLocal, setFilterLocal] = useState('all');

  const range = useMemo(() => {
    const preset = RANGE_PRESETS.find((p) => p.value === rangePreset);
    if (!preset || !preset.days) return { from: null, to: null };
    const to = new Date();
    const from = subDays(to, preset.days);
    return {
      from: from.toISOString().slice(0, 19),
      to: to.toISOString().slice(0, 19),
    };
  }, [rangePreset]);

  const buildQuery = useCallback((extra = {}) => {
    const params = new URLSearchParams();
    if (range.from) params.set('from', range.from);
    if (range.to) params.set('to', range.to);
    Object.entries(extra).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.set(k, v);
    });
    const qs = params.toString();
    return qs ? `?${qs}` : '';
  }, [range]);

  const loadAll = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      const [locRes, kpiRes, rankRes, serGlobalRes, serLocalRes] = await Promise.all([
        fetch(`${API_URL}/api/energia/locais`),
        fetch(`${API_URL}/api/energia/kpis${buildQuery()}`),
        fetch(`${API_URL}/api/energia/ranking${buildQuery()}`),
        fetch(`${API_URL}/api/energia/serie${buildQuery({ granularity })}`),
        fetch(`${API_URL}/api/energia/serie${buildQuery({ granularity, by_local: 'true' })}`),
      ]);
      if (!locRes.ok || !kpiRes.ok || !rankRes.ok) {
        throw new Error('Falha a carregar dados de energia');
      }
      const [loc, kp, rk, sg, sl] = await Promise.all([
        locRes.json(), kpiRes.json(), rankRes.json(), serGlobalRes.json(), serLocalRes.json(),
      ]);
      setLocais(Array.isArray(loc) ? loc : []);
      // Inicializar locais visíveis (todos por defeito) na primeira carga
      setVisibleCpes((prev) => {
        if (prev.size === 0 && Array.isArray(loc) && loc.length) {
          return new Set(loc.map((l) => l.cpe));
        }
        return prev;
      });
      setKpis(kp || null);
      setRanking(Array.isArray(rk) ? rk : []);
      setSerieGlobal(Array.isArray(sg) ? sg : []);
      setSerieByLocal(Array.isArray(sl) ? sl : []);
    } catch (err) {
      setError(err.message || 'Erro inesperado');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [buildQuery, granularity]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Mapa cpe -> local para joins
  const cpeToLocal = useMemo(() => {
    const m = {};
    locais.forEach((l) => { m[l.cpe] = l.local; });
    return m;
  }, [locais]);

  // Mapa cpe -> índice de cor (estável, baseado na ordem dos locais)
  const cpeColorMap = useMemo(() => {
    const m = {};
    locais.forEach((l, i) => { m[l.cpe] = COLORS[i % COLORS.length]; });
    return m;
  }, [locais]);

  const toggleCpe = (cpe) => {
    setVisibleCpes((prev) => {
      const next = new Set(prev);
      if (next.has(cpe)) next.delete(cpe); else next.add(cpe);
      return next;
    });
  };
  const selectOnlyCpe = (cpe) => setVisibleCpes(new Set([cpe]));
  const showAllCpes = () => setVisibleCpes(new Set(locais.map((l) => l.cpe)));

  // Pivot serie por local (apenas locais visíveis)
  const pivotedByLocal = useMemo(() => {
    const map = new Map();
    for (const r of serieByLocal) {
      if (!visibleCpes.has(r.cpe)) continue;
      if (!map.has(r.ts)) map.set(r.ts, { ts: r.ts });
      map.get(r.ts)[r.local] = (map.get(r.ts)[r.local] || 0) + (r.consumo_kwh || 0);
    }
    return Array.from(map.values()).sort((a, b) => a.ts.localeCompare(b.ts));
  }, [serieByLocal, visibleCpes]);

  const localNames = useMemo(
    () => locais.filter((l) => visibleCpes.has(l.cpe)).map((l) => l.local),
    [locais, visibleCpes]
  );

  // Linhas detalhadas para a tabela (cada bucket de granularidade × local)
  const tableData = useMemo(() => {
    // Ordenar por ts desc para mostrar o mais recente em cima
    const filtered = serieByLocal.filter((r) => visibleCpes.has(r.cpe));
    // Calcular desvio % vs ponto anterior para o mesmo CPE
    const byCpe = {};
    filtered.forEach((r) => {
      if (!byCpe[r.cpe]) byCpe[r.cpe] = [];
      byCpe[r.cpe].push(r);
    });
    Object.values(byCpe).forEach((arr) =>
      arr.sort((a, b) => a.ts.localeCompare(b.ts))
    );
    const rows = [];
    Object.entries(byCpe).forEach(([cpe, arr]) => {
      arr.forEach((r, idx) => {
        let desvio = 0;
        if (idx > 0) {
          const prev = arr[idx - 1].consumo_kwh || 0;
          if (prev > 0) desvio = ((r.consumo_kwh - prev) / prev) * 100;
          else if (r.consumo_kwh > 0) desvio = 100;
        }
        rows.push({
          ts: r.ts,
          local: r.local,
          cpe,
          consumo_kwh: r.consumo_kwh || 0,
          potencia_max_kw: r.potencia_max_kw || 0,
          desvio: parseFloat(desvio.toFixed(1)),
        });
      });
    });
    return rows.sort((a, b) => b.ts.localeCompare(a.ts));
  }, [serieByLocal, visibleCpes]);

  // Tabela filtrada por pesquisa + select de local
  const filteredTableData = useMemo(() => {
    return tableData.filter((row) => {
      const matchLocal = filterLocal === 'all' || row.cpe === filterLocal;
      const matchSearch = searchTerm === '' ||
        row.local.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row.cpe.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row.ts.includes(searchTerm);
      return matchLocal && matchSearch;
    });
  }, [tableData, filterLocal, searchTerm]);

  const tableTotals = useMemo(() => ({
    consumo: filteredTableData.reduce((s, r) => s + (r.consumo_kwh || 0), 0),
    pico: filteredTableData.reduce((m, r) => Math.max(m, r.potencia_max_kw || 0), 0),
    registos: filteredTableData.length,
  }), [filteredTableData]);

  const exportColumns = useMemo(() => ([
    { key: 'ts', label: granularity === 'hour' ? 'Data/Hora' : 'Data', type: granularity === 'hour' ? 'datetime' : 'date' },
    { key: 'local', label: 'Local' },
    { key: 'cpe', label: 'CPE' },
    { key: 'consumo_kwh', label: 'Consumo (kWh)', type: 'number', decimals: 2 },
    { key: 'potencia_max_kw', label: 'Pico (kW)', type: 'number', decimals: 2 },
    { key: 'desvio', label: 'Desvio (%)', type: 'percent', decimals: 1 },
  ]), [granularity]);

  const tickFormatter = (iso) => {
    try {
      const d = parseISO(iso);
      return granularity === 'hour'
        ? format(d, 'dd/MM HH:mm', { locale: pt })
        : format(d, 'dd/MM', { locale: pt });
    } catch { return iso; }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-gray-600">A carregar dados de energia...</div>
      </div>
    );
  }

  const hasData = locais.length > 0;

  return (
    <div className="space-y-6" data-testid="energia-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Energia</h1>
          <p className="text-gray-600 mt-1">
            Consumo elétrico baseado em Diagramas de Carga (E-Redes)
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={rangePreset} onValueChange={setRangePreset}>
            <SelectTrigger className="w-48" data-testid="energia-range-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANGE_PRESETS.map((p) => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Tabs value={granularity} onValueChange={setGranularity}>
            <TabsList data-testid="energia-granularity-tabs">
              <TabsTrigger value="hour" data-testid="energia-tab-hour">Hora</TabsTrigger>
              <TabsTrigger value="day" data-testid="energia-tab-day">Dia</TabsTrigger>
            </TabsList>
          </Tabs>
          <Button
            variant="outline"
            size="icon"
            onClick={loadAll}
            disabled={refreshing}
            data-testid="energia-refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/configuracoes/energia')}
            data-testid="energia-config-btn"
          >
            <Settings className="h-4 w-4 mr-2" /> Configurar
          </Button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <AlertCircle className="h-5 w-5" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {!hasData && (
        <Card data-testid="energia-empty-state">
          <CardContent className="p-10 text-center space-y-4">
            <div className="mx-auto w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center">
              <Zap className="h-7 w-7 text-[#003D7A]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Sem dados de energia carregados</h2>
              <p className="text-sm text-gray-600 mt-1">
                Carregue os ficheiros de Diagrama de Carga (E-Redes) para começar.
              </p>
            </div>
            <Button onClick={() => navigate('/configuracoes/energia')} data-testid="energia-empty-config-btn">
              <Settings className="h-4 w-4 mr-2" /> Ir para configuração
            </Button>
          </CardContent>
        </Card>
      )}

      {hasData && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              icon={Zap}
              label="Consumo Total"
              value={fmtKwh(kpis?.consumo_total_kwh)}
              sub={`${kpis?.samples?.toLocaleString('pt-PT')} amostras`}
              accent="bg-blue-50 text-[#003D7A]"
            />
            <KpiCard
              icon={TrendingUp}
              label="Pico de Potência"
              value={fmtKw(kpis?.potencia_max_kw)}
              sub="Máximo no período"
              accent="bg-amber-50 text-amber-600"
            />
            <KpiCard
              icon={Activity}
              label="Potência Média"
              value={fmtKw(kpis?.potencia_media_kw)}
              sub="Média no período"
              accent="bg-emerald-50 text-emerald-600"
            />
            <KpiCard
              icon={MapPin}
              label="Locais Monitorizados"
              value={fmtNumber(kpis?.locais)}
              sub="Pontos de medida"
              accent="bg-violet-50 text-violet-600"
            />
          </div>

          <ChartCard title={`Consumo Total (${granularity === 'hour' ? 'horário' : 'diário'})`}>
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={serieGlobal}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="ts"
                  tick={{ fontSize: 11 }}
                  stroke="#6b7280"
                  tickFormatter={tickFormatter}
                  minTickGap={30}
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  stroke="#6b7280"
                  tickFormatter={(v) => `${(v / 1000).toFixed(1)}k`}
                />
                <Tooltip
                  labelFormatter={tickFormatter}
                  formatter={(v, name) => [fmtKwh(v), name === 'consumo_kwh' ? 'Consumo' : name]}
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="consumo_kwh"
                  name="Consumo"
                  stroke="#003D7A"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2" data-testid="energia-ranking-card">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-gray-800">
                  Ranking de Locais por Consumo
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>#</TableHead>
                      <TableHead>Local</TableHead>
                      <TableHead className="text-right">Consumo</TableHead>
                      <TableHead className="text-right">Pico</TableHead>
                      <TableHead className="text-right">Pot. Média</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ranking.map((r, idx) => (
                      <TableRow key={r.cpe} data-testid={`energia-ranking-row-${idx}`}>
                        <TableCell className="font-medium">
                          <Badge variant="secondary">{idx + 1}</Badge>
                        </TableCell>
                        <TableCell className="font-medium text-gray-900">{r.local}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtKwh(r.consumo_total_kwh)}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtKw(r.potencia_max_kw)}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtKw(r.potencia_media_kw)}</TableCell>
                      </TableRow>
                    ))}
                    {!ranking.length && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-gray-500 py-6">
                          Sem dados no período selecionado.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <ChartCard title="Distribuição por Local">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={ranking} layout="vertical" margin={{ left: 10, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 11 }}
                    stroke="#6b7280"
                    tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                  />
                  <YAxis
                    type="category"
                    dataKey="local"
                    tick={{ fontSize: 10 }}
                    width={120}
                    stroke="#6b7280"
                  />
                  <Tooltip formatter={(v) => fmtKwh(v)} />
                  <Bar dataKey="consumo_total_kwh" name="Consumo" fill="#003D7A" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          <Card data-testid="energia-locais-toggle">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <CardTitle className="text-lg font-semibold text-gray-800">Locais Monitorizados</CardTitle>
                  <p className="text-xs text-gray-500 mt-1">
                    Clique para mostrar/ocultar · duplo-clique para selecionar apenas um · {visibleCpes.size}/{locais.length} visíveis
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={showAllCpes} data-testid="energia-show-all-btn">
                  Mostrar todos
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {locais.map((l) => {
                  const isVisible = visibleCpes.has(l.cpe);
                  const color = cpeColorMap[l.cpe];
                  const localSerie = serieByLocal.filter((r) => r.cpe === l.cpe);
                  const total = localSerie.reduce((s, r) => s + (r.consumo_kwh || 0), 0);
                  return (
                    <div
                      key={l.cpe}
                      onClick={() => toggleCpe(l.cpe)}
                      onDoubleClick={() => selectOnlyCpe(l.cpe)}
                      className={`cursor-pointer rounded-lg border p-3 transition-all ${
                        isVisible ? 'shadow-sm' : 'opacity-50 grayscale hover:opacity-75'
                      }`}
                      style={{ borderColor: isVisible ? color : '#e5e7eb', borderLeftWidth: 4 }}
                      data-testid={`energia-local-toggle-${l.cpe}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            {isVisible ? <Eye className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" /> : <EyeOff className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />}
                            <p className="text-sm font-medium text-gray-900 truncate">{l.local}</p>
                          </div>
                          <p className="text-[10px] text-gray-400 font-mono truncate">{l.cpe}</p>
                        </div>
                        <Badge variant="outline" className="text-[10px] flex-shrink-0">{l.owner || '—'}</Badge>
                      </div>
                      <div className="mt-2 flex items-end justify-between">
                        <span className="text-xs text-gray-500">Período</span>
                        <span className="text-base font-bold text-gray-900 tabular-nums">{fmtKwh(total)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <ChartCard title="Comparativo por Local">
            {visibleCpes.size === 0 ? (
              <div className="text-center text-gray-500 py-12">Nenhum local selecionado</div>
            ) : (
            <ResponsiveContainer width="100%" height={360}>
              <LineChart data={pivotedByLocal}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="ts"
                  tick={{ fontSize: 11 }}
                  stroke="#6b7280"
                  tickFormatter={tickFormatter}
                  minTickGap={30}
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  stroke="#6b7280"
                  tickFormatter={(v) => `${(v / 1000).toFixed(1)}k`}
                />
                <Tooltip
                  labelFormatter={tickFormatter}
                  formatter={(v) => fmtKwh(v)}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {locais.filter((l) => visibleCpes.has(l.cpe)).map((l) => (
                  <Line
                    key={l.cpe}
                    type="monotone"
                    dataKey={l.local}
                    stroke={cpeColorMap[l.cpe]}
                    strokeWidth={2}
                    dot={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
            )}
          </ChartCard>

          {/* Tabela filtrável + exportação */}
          <Card data-testid="energia-table-card">
            <CardContent className="pt-6">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                  <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                    <Activity className="h-5 w-5 text-amber-500" />
                    Registos de Consumo Energético
                    <Badge className="ml-1 bg-green-100 text-green-800">Dados Reais</Badge>
                  </h2>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Filter className="h-4 w-4" />
                      <span>{filteredTableData.length} registos</span>
                    </div>
                    <ExportMenu
                      columns={exportColumns}
                      rows={filteredTableData}
                      filename="consumo_energia"
                      title="Relatório de Consumo Energético"
                      subtitle={`Granularidade: ${granularity === 'hour' ? 'Horária' : 'Diária'} · Locais: ${visibleCpes.size}`}
                      testId="energia-export"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 bg-gray-50 rounded-lg">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Pesquisar local, CPE ou data..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                      data-testid="energia-search-input"
                    />
                  </div>
                  <Select value={filterLocal} onValueChange={setFilterLocal}>
                    <SelectTrigger data-testid="energia-filter-local">
                      <SelectValue placeholder="Todos os locais" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os locais</SelectItem>
                      {locais.filter((l) => visibleCpes.has(l.cpe)).map((l) => (
                        <SelectItem key={l.cpe} value={l.cpe}>{l.local}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="rounded-md border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>{granularity === 'hour' ? 'Data/Hora' : 'Data'}</TableHead>
                        <TableHead>Local</TableHead>
                        <TableHead className="font-mono text-xs">CPE</TableHead>
                        <TableHead className="text-right">Consumo (kWh)</TableHead>
                        <TableHead className="text-right">Pico (kW)</TableHead>
                        <TableHead className="text-right">Desvio (%)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredTableData.length > 0 ? filteredTableData.slice(0, 500).map((row, idx) => (
                        <TableRow key={`${row.cpe}-${row.ts}-${idx}`} className="hover:bg-gray-50" data-testid={`energia-table-row-${idx}`}>
                          <TableCell className="font-medium">
                            {format(parseISO(row.ts), granularity === 'hour' ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy', { locale: pt })}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: cpeColorMap[row.cpe] }} />
                              {row.local}
                            </div>
                          </TableCell>
                          <TableCell className="font-mono text-xs text-gray-500">{row.cpe}</TableCell>
                          <TableCell className="text-right font-mono tabular-nums">{fmtNumber(row.consumo_kwh, 2)}</TableCell>
                          <TableCell className="text-right font-mono tabular-nums">{fmtNumber(row.potencia_max_kw, 2)}</TableCell>
                          <TableCell className="text-right">
                            <span className={`font-semibold ${row.desvio > 0 ? 'text-red-600' : row.desvio < 0 ? 'text-emerald-600' : 'text-gray-600'}`}>
                              {row.desvio > 0 ? '+' : ''}{fmtNumber(row.desvio, 1)}%
                            </span>
                          </TableCell>
                        </TableRow>
                      )) : (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                            Nenhum registo encontrado
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                    {filteredTableData.length > 0 && (
                      <TableFooter>
                        <TableRow className="bg-gray-100 font-semibold">
                          <TableCell colSpan={3} className="text-right">
                            Total ({tableTotals.registos} registos):
                          </TableCell>
                          <TableCell className="text-right font-mono text-amber-700">{fmtNumber(tableTotals.consumo, 2)} kWh</TableCell>
                          <TableCell className="text-right font-mono text-amber-700">{fmtNumber(tableTotals.pico, 2)} kW</TableCell>
                          <TableCell />
                        </TableRow>
                      </TableFooter>
                    )}
                  </Table>
                  {filteredTableData.length > 500 && (
                    <div className="text-xs text-gray-500 text-center py-2 bg-gray-50">
                      A mostrar 500 dos {filteredTableData.length} registos. Use a exportação para obter o conjunto completo.
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default Energia;
